import {AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, Renderer2} from '@angular/core';
import {MenuItem} from 'primeng/api';
import {PrimeNG} from 'primeng/config';
import {AppComponent} from './app.component';
import {ActivatedRoute, Router} from "@angular/router";
import {BehaviorSubject, Subscription} from "rxjs";
import {MvsObjectNavigationEntry} from "../../../../features/core/shared/navigation/mvs-object-navigation-entry";
import {PageComponent} from "../../../../features/core/shared/mvs-page/page.component";
import {ObjectIdentifier} from "../../../../features/core/shared/basic/object-identifier";
import {NavigationItem} from "../../../../features/core/shared/dto/navigation/navigation-item";
import {MenuService} from "../../../../features/core/shared/service/menu/menu.service";
import {MvsObjectNavigationService} from "../../../../features/core/shared/navigation/mvs-object-navigation.service";
import {PageService} from "../../../../features/core/shared/service/page/page.service";
import {PageBehaviorInterface} from "../../../../features/core/shared/service/page/page-behavior.interface";


@Component({
    selector: 'app-main',
    templateUrl: './app.main.component.html',
    styleUrls: ['./app.main.component.scss'],
    standalone: false
})
export class AppMainComponent implements AfterViewInit, OnInit, OnDestroy {

    topbarMenuActive: boolean;

    menuActive: boolean;

    staticMenuDesktopInactive: boolean;

    mobileMenuActive: boolean;

    menuClick: boolean;

    mobileTopbarActive: boolean;

    topbarRightClick: boolean;

    topbarItemClick: boolean;

    activeTopbarItem: string;

    documentClickListener: () => void;

    configActive: boolean;

    configClick: boolean;

    rightMenuActive: boolean;

    menuHoverActive = false;

    searchClick = false;

    search = false;

    currentInlineMenuKey: string;

    inlineMenuActive: any[] = [];

    inlineMenuClick: boolean;

    isConfigRoute: boolean = false;
    showTopBar: boolean = false;
    showPadding: boolean = false;

    routerSubscription: Subscription;
    showBreadcrumb: boolean;
    pageChangeSubscription: Subscription;
    pageDestroySubscription: Subscription;
    navigationObjectSubscription: Subscription;
    navigationOverlaySubscription: Subscription;
    navigationObject: BehaviorSubject<MvsObjectNavigationEntry>;
    currentPage: PageComponent;
    currentObject: ObjectIdentifier;
    selectedNavItem: string;
    navigationItems: NavigationItem[];
    currentOverlayObject: MvsObjectNavigationEntry;
    breadcrumbItems: MenuItem[];
    navigationParamSubscription: Subscription;
    preservedMainNavItem: string;
    // [2026-03-29] Fix: Preserve config page reference across object navigation round-trips
    // When opening an object (e.g. clicking a notification type from config page), the config
    // page is destroyed and MvsObjectNavigationMainComponent/MvsGenericObjectPageComponent take
    // over as currentPage. This field preserves the original config page so it can be restored
    // when the user navigates back via breadcrumb.
    preservedMainPage: PageComponent;

    constructor(
        public renderer: Renderer2,
        private menuService: MenuService,
        private primengConfig: PrimeNG,
        public app: AppComponent,
        public router: Router,
        protected navigationService: MvsObjectNavigationService,
        private activatedRoute: ActivatedRoute,
        public pageService: PageService,
        private cdr: ChangeDetectorRef
        ) {
    }

    ngOnInit() {

        this.applyStoredTopbarTheme();

        this.menuActive = this.isStatic() && !this.isMobile();

        //check for breadcrumb visibility
        this.subscribePageChange();
        this.subscribePageNavigation();
    }

    /**
     * Seeds the top bar theme from the stored user settings before this component's template is
     * checked for the first time.
     *
     * The layout wrapper binds the theme into its class list ('layout-topbar-' + app.topbarTheme).
     * AppTopBarComponent used to be the first to apply the stored theme, but it does so in its
     * ngAfterViewInit - after this template had already been checked with the default 'white'.
     * The bound class therefore changed between the check and Angular's verification pass, which
     * is exactly the NG0100 (ExpressionChangedAfterItHasBeenChecked) reported on every page load.
     *
     * The settings live in localStorage and are available synchronously, so reading them here
     * makes the bound value final before the first check. The top bar still applies the same
     * theme later (together with the logo and icon colours); it then assigns an unchanged value.
     * The path where no settings are stored yet loads them over HTTP and applies them in a later
     * change detection cycle, which is unaffected.
     */
    private applyStoredTopbarTheme(): void {
        try {
            const storedSettings = localStorage.getItem('ui.userSettings');
            if (!storedSettings) {
                return;
            }

            const layoutJson = JSON.parse(storedSettings)?.layoutJson;
            if (!layoutJson) {
                return;
            }

            const theme = JSON.parse(layoutJson)?.topbarTheme;
            const themeName = typeof theme === 'string' ? theme : theme?.name;
            if (themeName) {
                this.app.topbarTheme = themeName;
            }
        } catch {
            // Unreadable settings must not keep the shell from starting - keep the default theme.
        }
    }

    ngAfterViewInit() {
        // hides the horizontal submenus or top menu if outside is clicked
        this.documentClickListener = this.renderer.listen('body', 'click', () => {
            if (!this.topbarItemClick) {
                this.activeTopbarItem = null;
                this.topbarMenuActive = false;
            }

            if (!this.menuClick && (this.isHorizontal() || this.isSlim())) {
                this.menuService.reset();
            }

            if (this.configActive && !this.configClick) {
                this.configActive = false;
            }

            if (!this.menuClick) {
                if (this.mobileMenuActive) {
                    this.mobileMenuActive = false;
                }

                if (this.isOverlay()) {
                    this.menuActive = false;
                }

                this.menuHoverActive = false;
                this.unblockBodyScroll();
            }

            if (!this.searchClick) {
                this.search = false;
            }

            if (this.inlineMenuActive[this.currentInlineMenuKey] && !this.inlineMenuClick) {
                this.inlineMenuActive[this.currentInlineMenuKey] = false;
            }

            this.inlineMenuClick = false;
            this.searchClick = false;
            this.configClick = false;
            this.topbarItemClick = false;
            this.topbarRightClick = false;
            this.menuClick = false;
        });
    }


    onMenuButtonClick(event) {
        this.menuActive = !this.menuActive;
        this.topbarMenuActive = false;
        this.topbarRightClick = true;
        this.menuClick = true;

        if (this.isDesktop()) {
            this.staticMenuDesktopInactive = !this.staticMenuDesktopInactive;
        } else {
            this.mobileMenuActive = !this.mobileMenuActive;
            if (this.mobileMenuActive) {
                this.blockBodyScroll();
            } else {
                this.unblockBodyScroll();
            }
        }

        event.preventDefault();
    }

    onTopbarMobileButtonClick(event) {
        this.mobileTopbarActive = !this.mobileTopbarActive;
        event.preventDefault();
    }

    onRightMenuButtonClick(event) {
        this.rightMenuActive = !this.rightMenuActive;
        event.preventDefault();
    }

    onMenuClick(event: NavigationItem) {

        if (event.customFunction){
            event.customFunction();
            return;
        }

        if (this.selectedNavItem == event.action) {
            //same nav item is selected

            if (event.toggleable == false) {
                //same nav item is selected and nav item can not be toggled if we click again so we do nothing
                return;
            }

            // otherwise nav item can be toggled
            this.selectedNavItem = null;

            // TM-1000001554: While an overlay is open, tab state lives on the
            // service — do NOT touch the URL. Keeping the URL static preserves
            // the main page's tab so it can be restored when the overlay closes.
            if (this.currentOverlayObject) {
                this.navigationService.setOverlayNavItem(null);
                return;
            }

            this.removeNavItemParam();
            return;
        }

        // different nav item is selected
        this.selectedNavItem = event.action;

        // TM-1000001554: Route tab changes through the service while an overlay
        // is open, so the URL's `navItem` (which belongs to the main page) stays
        // unchanged. The active overlay's page subscribes to `overlayNavItem$`.
        if (this.currentOverlayObject) {
            this.navigationService.setOverlayNavItem(this.selectedNavItem);
            return;
        }

        const queryParams = {};
        queryParams['navItem'] =  this.selectedNavItem;
        this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: queryParams,
            queryParamsHandling: 'merge'
        });
    }

    removeNavItemParam() {
        const queryParams = { ...this.activatedRoute.snapshot.queryParams };
        delete queryParams['navItem'];
        this.removeParam(queryParams);
    }

    removeParam(queryParams: object) {
        this.router.navigate([], { queryParams: queryParams });
    }

    onSearchKeydown(event) {
        if (event.keyCode === 27) {
            this.search = false;
        }
    }

    onInlineMenuClick(event, key) {
        if (key !== this.currentInlineMenuKey) {
            this.inlineMenuActive[this.currentInlineMenuKey] = false;
        }

        this.inlineMenuActive[key] = !this.inlineMenuActive[key];
        this.currentInlineMenuKey = key;
        this.inlineMenuClick = true;
    }

    onTopbarItemClick(event, item) {
        this.topbarItemClick = true;

        if (this.activeTopbarItem === item) {
            this.activeTopbarItem = null;
        }
        else {
            this.activeTopbarItem = item;
        }

        if (item === 'search') {
            this.search = !this.search;
            this.searchClick = !this.searchClick;
        }

        event.preventDefault();
    }

    onTopbarSubItemClick(event) {
        event.preventDefault();
    }

    onRTLChange(event) {
        this.app.isRTL = event.checked;
    }

    onRippleChange(event) {
        // this.app.ripple = event.checked;
        // this.primengConfig.ripple = event.checked;
    }

    onConfigClick(event) {
        this.configClick = true;
    }

    isDesktop() {
        return window.innerWidth > 991;
    }

    isMobile() {
        return window.innerWidth <= 991;
    }

    isOverlay() {
        return this.app.menuMode === 'overlay';
    }

    isStatic() {
        return this.app.menuMode === 'static';
    }

    isHorizontal() {
        return this.app.menuMode === 'horizontal';
    }

    isSlim() {
        return this.app.menuMode === 'slim';
    }

    blockBodyScroll(): void {
        if (document.body.classList) {
            document.body.classList.add('blocked-scroll');
        } else {
            document.body.className += ' blocked-scroll';
        }
    }

    unblockBodyScroll(): void {
        if (document.body.classList) {
            document.body.classList.remove('blocked-scroll');
        } else {
            document.body.className = document.body.className.replace(new RegExp('(^|\\b)' +
                'blocked-scroll'.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
        }
    }

    checkConfigRoute(component: PageComponent): boolean {

        if (component['isConfigurationPage']) {
            const pageBehavior = <PageBehaviorInterface>component;
            return pageBehavior.isConfigurationPage();
        } else {
            return false;
        }

    }

    ngOnDestroy() {
        if (this.documentClickListener) {
            this.documentClickListener();
        }
       if (this.routerSubscription) {
            this.routerSubscription.unsubscribe();
        }
        this.isConfigRoute = false;

       if (this.pageChangeSubscription) {
           this.pageChangeSubscription.unsubscribe();
       }

        if (this.pageDestroySubscription) {
            this.pageDestroySubscription.unsubscribe();
        }


       if (this.navigationObjectSubscription) {
            this.navigationObjectSubscription.unsubscribe();
       }

        if (this.navigationParamSubscription) {
            this.navigationParamSubscription.unsubscribe();
        }


       if (this.navigationOverlaySubscription) {
            this.navigationOverlaySubscription.unsubscribe();
       }
    }

    private isBreadcrumbVisible(component: PageComponent): boolean {

        if (component['isBreadcrumbVisible']) {
            const pageBehavior = <PageBehaviorInterface>component;
            return pageBehavior.isBreadcrumbVisible();
        } else {
            return true;
        }
    }

    subscribePageChange() {
        this.pageChangeSubscription = this.pageService.pageChange.subscribe((page: PageComponent) => {
            // Handle the breadcrumb visibility as provided by the respective component
            if (!page['isOverlay']) {
                this.currentPage = page;
            }
            this.navigationItems = null;
            this.pageService.breadcrumbItems = null;
            this.showBreadcrumb = this.isBreadcrumbVisible(page);

            // Override: force breadcrumb visible when object navigation is active
            if (!this.showBreadcrumb && this.navigationObject) {
                const navEntry = this.navigationObject.getValue();
                if (navEntry && navEntry.objectIdentifier) {
                    this.showBreadcrumb = true;
                }
            }

            this.isConfigRoute = this.checkConfigRoute(page);
            this.handleNavigationItems(page);
            this.handleBreadcrumbItems(page);
            this.showTopBar = this.checkTopBarVisibility(page);
            this.showPadding = this.checkPaddingVisibility(page);
            this.cdr.detectChanges();
        });

        //TODO: Daud, we need to discuss this
        // this.pageDestroySubscription = this.pageService.pageDestroyed$.subscribe(page => {
        //     this.navigationItems = null;
        //     this.pageService.breadcrumbItems = null;
        //     this.selectedNavItem = null;
        //     this.currentPage = null;
        // });
    }

    handleNavigationItems(page: PageComponent) {
        this.navigationItems = this.checkNavComponent(page);
        if (!this.navigationItems?.length) {
            return;
        }

        const queryParams = this.activatedRoute.snapshot.queryParams;
        const initialValue = this.getNavItemInitialValue();

        const isOverlay = !!page['isOverlay'];

        if (isOverlay) {
            if (initialValue) {
                this.selectedNavItem = initialValue;
            } else {
                this.selectedNavItem = this.navigationItems[0].action;
            }

            // TM-1000001554: Seed the overlay nav channel with the overlay's
            // default tab so the overlay page subscription (see
            // CrCustomerObjectPage / ObjectBaseModeComponent) sees the initial
            // value and stays in sync with the topbar highlight.
            this.navigationService.setOverlayNavItem(this.selectedNavItem);

        } else {
            //1. check if we have a preserved main object value
            //2. if nothing in params then take initial value
            //3. if in params, take params one
            if (this.preservedMainNavItem) {
                this.selectedNavItem = this.preservedMainNavItem;
                this.preservedMainNavItem = null;

            } else if (initialValue && !queryParams['navItem']) {
                this.selectedNavItem = initialValue;
            } else if (queryParams['navItem']) {
                this.selectedNavItem = queryParams['navItem'];
            } else {
                this.onMenuClick(this.navigationItems[0]);
                this.selectedNavItem = null;
            }
        }

        this.navigateToRoute();

    }

    navigateToRoute(): void {

        if (this.currentOverlayObject) {
            return;
        }

        const currentNavItem = this.activatedRoute.snapshot.queryParams['navItem'];

        if (currentNavItem === this.selectedNavItem) {
            return;
        }

        const queryParams = {
            ...this.activatedRoute.snapshot.queryParams
        };

        if (this.selectedNavItem) {
            queryParams['navItem'] = this.selectedNavItem;
        } else {
            delete queryParams['navItem'];
        }

        this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams
        });
    }
    handleBreadcrumbItems(page: PageComponent) {
        this.breadcrumbItems = this.pageService.breadcrumbItems;
    }

    private checkTopBarVisibility(component: PageComponent): boolean {

        if (component['isTopBarVisible']) {
            const pageBehavior = <PageBehaviorInterface>component;
            return pageBehavior.isTopBarVisible();
        } else {
            return null;
        }
    }

    private checkPaddingVisibility(component: PageComponent): boolean {

        if (component['isPaddingVisible']) {
            const pageBehavior = <PageBehaviorInterface>component;
            return pageBehavior.isPaddingVisible();
        } else {
            return null;
        }
    }

    getNavItemInitialValue() {
        if (this.navigationItems?.length) {

            const value = this.navigationItems.find(item => item.default == true);
            if (value) {
                return value.action;
            }
            return null;
        }
        return null;
    }

    private checkNavComponent(component: PageComponent): NavigationItem[] {

        if (component['isNavComponent']) {
            const pageBehavior = <PageBehaviorInterface>component;
            return pageBehavior.isNavComponent();
        } else {
            return null;
        }
    }

    private checkBreadcrumbItems(component: PageComponent): MenuItem[] {

        if (component['isBreadcrumbComponent']) {
            const pageBehavior = <PageBehaviorInterface>component;
            return pageBehavior.isBreadcrumbComponent();
        } else {
            return null;
        }
    }

    subscribePageNavigation() {
        this.navigationObject = this.navigationService.getNavigationBehaviourSubject('main');

        // react on navigation node change
        this.navigationObjectSubscription = this.navigationObject.subscribe(navigationObject => {
            this.refreshObject(navigationObject);

            // Force breadcrumb visible when object navigation is active
            if (navigationObject && navigationObject.objectIdentifier) {
                this.showBreadcrumb = true;
            }
        });

        //page can also be loaded in overlay, so we need to check it as well for navigation items
        this.navigationOverlaySubscription = this.navigationService.navigationObjectOverlay.subscribe(navigationObject => {
            this.refreshOverlayObject(navigationObject[navigationObject?.length - 1]);
        })
    }

    refreshOverlayObject(navObject: MvsObjectNavigationEntry) {

        if (navObject) {

            if (!this.currentOverlayObject) {
                this.preservedMainNavItem = this.selectedNavItem;
            }

            this.currentOverlayObject = navObject;
        } else if (!navObject && this.currentOverlayObject) {
            this.pageService.setActivePage(this.currentPage);
            this.navigationItems = null;
            this.currentOverlayObject = null;
            if (this.currentPage) {
                this.handleNavigationItems(this.currentPage);
            }
        }
    }
    // [2026-03-29] Fix: Restore navigation items and page header when navigating back from object to config page
    //
    // Problem: When clicking breadcrumb to go back from an object view to the config page,
    // navigation items and page header disappeared because:
    // 1. MvsObjectNavigationMainComponent called setActivePage() even when navigationObject was null,
    //    overwriting currentPage with a component that has no nav items.
    // 2. MvsGenericObjectPageComponent also extends PageComponent and calls setActivePage(),
    //    further overwriting currentPage after each cdr.detectChanges() cycle.
    // 3. refreshObject() previously only set currentObject=null without restoring the original page.
    //
    // Solution: Mirror the overlay pattern (refreshOverlayObject). Preserve the config page reference
    // when opening an object, and restore it when navigating back. Only save pages that have
    // navigation items (to avoid saving MvsGenericObjectPageComponent which has none). Keep
    // preservedMainPage across round-trips so it survives re-entrant setActivePage() calls.
    refreshObject(navObject: MvsObjectNavigationEntry) {

        if (navObject) {
            // Save the current page before object navigation overwrites it.
            // Only save pages that have navigation items (real pages like config pages),
            // not child page components like MvsGenericObjectPageComponent that also
            // call setActivePage() but have no nav items.
            if (!this.currentObject && this.currentPage) {
                const pageNavItems = this.checkNavComponent(this.currentPage);
                if (pageNavItems?.length) {
                    this.preservedMainPage = this.currentPage;
                }
            }
            this.currentObject = navObject.objectIdentifier;

        } else {
            // Navigation object closed — restore the preserved page so its
            // navigation items and page header are visible again.
            this.currentObject = null;

            if (this.preservedMainPage) {
                this.pageService.setActivePage(this.preservedMainPage);
                this.navigationItems = null;
                this.handleNavigationItems(this.preservedMainPage);
                // Do NOT clear preservedMainPage here — keep it for subsequent round-trips.
                // It will be overwritten on the next OPEN only if currentPage has nav items.
            }
        }
    }

}
